/**
 * Voice AI — Media Library Service
 * Singleton modal orchestrator following the confirmService pattern.
 * Any component can `await mediaLibraryService.show(options)` to open the
 * media library modal and get back selected files (or null on cancel).
 *
 * A single <voice-media-library> in app-shell.js listens and renders.
 */

class MediaLibraryService {
  _resolve = null;
  _listener = null;

  /** Called once by <voice-media-library> to subscribe */
  onStateChange(callback) {
    this._listener = callback;
  }

  /**
   * Open the media library modal. Returns a Promise.
   * @param {Object} [options]
   * @param {string} [options.fileType] - Pre-select filter pill (audio/image/video/document/null=All)
   * @param {string} [options.tags] - Default tags for uploads (e.g. 'realism,interaction')
   * @param {string} [options.uploadedBy] - Source context for uploads
   * @param {boolean} [options.multiple=true] - Multi-select mode
   * @param {number[]} [options.selectedIds] - Pre-checked media IDs
   * @param {string} [options.accept] - File input accept string (e.g. 'audio/*')
   * @returns {Promise<{files: Object[]}|null>} Selected files or null on cancel
   */
  show(options = {}) {
    // Cancel any pending dialog
    if (this._resolve) {
      this._resolve(null);
      this._resolve = null;
    }

    return new Promise(resolve => {
      this._resolve = resolve;
      this._listener?.({
        open: true,
        fileType: options.fileType || null,
        tags: options.tags || '',
        uploadedBy: options.uploadedBy || '',
        multiple: options.multiple !== false,
        selectedIds: options.selectedIds || [],
        accept: options.accept || '',
      });
    });
  }

  /** Called by the component when user confirms selection */
  select(files) {
    this._resolve?.({ files });
    this._resolve = null;
    this._listener?.({ open: false });
  }

  /** Called by the component on cancel/ESC/overlay click */
  cancel() {
    this._resolve?.(null);
    this._resolve = null;
    this._listener?.({ open: false });
  }
}

export const mediaLibraryService = new MediaLibraryService();
